import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import {
  resetPageViewTracking,
  useTrackPageMetadata,
} from '../analytics/cohesion';
import { useAuth } from '../components/Auth';
import { syncUtagData } from '../tagging/tealium/utag-data';
import { useCurrentPage } from './CurrentPageProvider';

// #region JSDoc Types

/**
 * @typedef {object} PageTrackingConfig
 * @property {string | ((userCategory: import('../components/Auth').UserCategory) => string)} pageName - Page name for analytics (e.g., "Checkout", "Landing Page") or function that returns pageName based on user category
 */

/**
 * @typedef {object} PageTrackerProps
 * @property {PageTrackingConfig} tracking - Tracking configuration from ROUTES
 * @property {React.ReactNode} children - Page content
 */

// #endregion

/**
 * Wrapper component that fires page view analytics when a route renders.
 *
 * This component handles the SPA challenge of tracking page views:
 * - Only fires when the page actually renders (not during auth redirects)
 * - Automatically detects route changes via useLocation()
 * - Resets page-view tracking state on navigation
 * - Keeps window.utag_data in sync for Tealium
 *
 * @param {PageTrackerProps} props
 *
 * @example
 * <Route path="/checkout" element={
 *   <PageTracker tracking={{ pageName: 'Checkout' }}>
 *     <CheckoutPage />
 *   </PageTracker>
 * }/>
 */
export function PageTracker({ tracking, children }) {
  const location = useLocation();
  const trackPageMetadata = useTrackPageMetadata();
  const { setPageName } = useCurrentPage();
  const { userCategory } = useAuth();

  const lastPathnameRef = useRef(/** @type {string | null} */ (null));

  // Resolve pageName - support both string and function
  const pageName =
    typeof tracking.pageName === 'function'
      ? tracking.pageName(userCategory)
      : tracking.pageName;

  useEffect(() => {
    // Reset page-view tracking state when the route changes
    if (lastPathnameRef.current !== location.pathname) {
      resetPageViewTracking();
      lastPathnameRef.current = location.pathname;
    }

    setPageName(pageName);

    // Sync window.utag_data so Tealium always has current page info
    syncUtagData({ pageName });

    // Fire the Cohesion PageMetadata event
    trackPageMetadata();
  }, [location.pathname, pageName, setPageName, trackPageMetadata]);

  return children;
}
